import { useEffect, useState } from "react";
import styles from "./ConsentManagement.module.scss";
import { Link, useNavigate } from "react-router-dom";
import { IoIosArrowForward, IoMdArrowBack } from "react-icons/io";
import AfterOnboardAPI from "../../services/AfterOnboardAPI";
import { handlehideContextualLoader, handleShowContextualLoader } from "../../utils/contextualLoaderTrigger";
import moment from "moment/moment";
import { clevertap_consent_preferences_screen, clevertap_consent_preferences_screen_click } from "../../lib/clevertap-events";
import Swal from "sweetalert2";

export default function ConsentManagement() {
  const [mandatory, setMandatory] = useState([]);
  const [optional, setOptional] = useState([]);
  const [lastUpdated, setLastUpdated] = useState("");

  const { consentPreferencesAPI, consentPreferencesUpdateAPI } = new AfterOnboardAPI();

  const navigate = useNavigate();

  useEffect(() => {
    fetchAPI();
  }, []);

  const fetchAPI = async () => {
    handleShowContextualLoader();

    await clevertap_consent_preferences_screen();

    try {
      const res = await consentPreferencesAPI();

      if (res?.success && res?.data) {
        setMandatory(res.data.mandatory || []);
        setOptional(res.data.optional || []);
        setLastUpdated(res.data.lastUpdated || "");
      }
    } catch (error) {
      console.error("Failed to fetch consent preferences:", error);
    } finally {
      handlehideContextualLoader();
    }
  };

  const handleOptionalChange = async (code) => {
    handleShowContextualLoader();

    try {
      const currentItem = optional.find((item) => item.code === code);

      if (!currentItem) return;

      const isCurrentlyActive = currentItem.status === "1" || currentItem.status === 1;

      const newStatus = isCurrentlyActive ? "0" : "1";

      // Call update API
      const res = await consentPreferencesUpdateAPI(code, newStatus);

      // Update UI only after successful API call
      if (res?.success) {
        setOptional((prev) =>
          prev.map((item) =>
            item.code === code
              ? {
                  ...item,
                  status: newStatus,
                }
              : item,
          ),
        );
      }

      // CleverTap
      if (currentItem.ct_event_name) {
        await clevertap_consent_preferences_screen_click({
          CTA: "Toggle",
          [currentItem.ct_event_name]: newStatus === "1" ? "on" : "off",
        });
      }

      handlehideContextualLoader();

      await Swal.fire({
        icon: "success",
        text: "Your preferences have been updated successfully",
        timer: 5000,
        showConfirmButton: false,
        timerProgressBar: true,
      });
    } catch (error) {
      console.error("Failed to update consent preference:", error);
    } finally {
      handlehideContextualLoader();
    }
  };

  return (
    <div className={`${styles.wrapper} wrapper auto-height`}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backButton} type="button" onClick={() => navigate("/profile")}>
          <IoMdArrowBack />
        </button>

        <h1>Consent Management</h1>
      </div>

      {/* Compliance Banner */}
      <div className={styles.complianceCard}>
        <div className={styles.complianceLabel}>RBI COMPLIANT</div>
        <div className={styles.complianceTitle}>Secure Financial Permissions</div>
      </div>

      {/* Required */}
      {mandatory.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>◈</span>
            Required
          </div>

          <div className={styles.optionsCard}>
            {mandatory.map((item) => (
              <ConsentItem key={item.code} title={item.name} required />
            ))}
          </div>
        </section>
      )}

      {/* Optional */}
      {optional.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>☷</span>
            Optional
          </div>

          <div className={styles.optionsCard}>
            {optional.map((item) => (
              <ConsentItem key={item.code} title={item.name} toggle checked={item.status === "1" || item.status === 1} onChange={() => handleOptionalChange(item.code)} />
            ))}
          </div>
        </section>
      )}

      {/* History Button */}
      <section className={styles.historyBlock}>
        {lastUpdated && <p>Last Updated: {moment(lastUpdated).format("DD MMM YYYY")}</p>}

        <Link
          to="/consent-history"
          onClick={async (e) => {
            e.preventDefault();

            await clevertap_consent_preferences_screen_click({
              CTA: "consent history",
            });

            navigate("/consent-history");
          }}
        >
          <span>Consent History</span>
          <IoIosArrowForward />
        </Link>
      </section>
    </div>
  );
}

function ConsentItem({ title, required = false, toggle = false, checked = false, onChange }) {
  return (
    <div className={styles.item}>
      <div className={styles.itemLeft}>
        <span className={styles.itemTitle}>{title}</span>
      </div>

      {required && <span className={styles.requiredBadge}>Required</span>}

      {toggle && (
        <button type="button" className={`${styles.switch} ${checked ? styles.switchActive : ""}`} onClick={onChange} aria-pressed={checked}>
          <span className={styles.switchThumb} />
        </button>
      )}
    </div>
  );
}
